/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, Box, Typography, Avatar, IconButton, Chip } from '@mui/material';
import { FiX, FiEye, FiEyeOff, FiUser } from 'react-icons/fi';

const PolicyViewers = ({ policyUsers, setShowParticipants }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [seenUsers, setSeenUsers] = useState([]);
    const [unseenUsers, setUnseenUsers] = useState([]);

    useEffect(() => {
        if (policyUsers) {
            const seen = policyUsers.filter((user) => user.seen === true);
            const unseen = policyUsers.filter((user) => user.seen === false);
            setSeenUsers(seen);
            setUnseenUsers(unseen);
        }
    }, [policyUsers]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const timeDiffer = (now, date) => {
        var diffMs = now - date; // milliseconds between now & Christmas
        var diffDays = Math.floor(diffMs / 86400000); // days
        var diffHrs = Math.floor((diffMs % 86400000) / 3600000); // hours
        var diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000); // minutes
        return diffDays > 0
            ? `${diffDays} day${diffDays > 1 ? 's' : ''}, ${diffHrs} hour${diffHrs > 1 ? 's' : ''} ago`
            : diffHrs > 0
            ? `${diffHrs} hour${diffHrs > 1 ? 's' : ''} ago`
            : `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    };

    const UserCard = ({ user, isSeen }) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                backgroundColor: '#fff',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                    backgroundColor: '#f8f9fa'
                }
            }}
        >
            <Box sx={{ position: 'relative' }}>
                <Avatar
                    src={user.profile_image}
                    sx={{
                        width: 40,
                        height: 40,
                        border: '1px solid #e9ecef'
                    }}
                >
                    {user.first_name ? user.first_name.charAt(0) : <FiUser size={20} />}
                </Avatar>
                {/* Status indicator */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -2,
                        right: 0,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: isSeen ? '#28a745' : '#ffc107',
                        border: '1px solid #fff'
                    }}
                />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {user.first_name || 'Unknown User'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    {user?.job_title?.[0]?.title || user?.role?.role_type || 'No designation'}
                </Typography>
            </Box>

            <Box>
                {isSeen ? (
                    <Box sx={{ textAlign: 'right' }}>
                        <Chip
                            label="Seen"
                            size="small"
                            sx={{
                                backgroundColor: '#e8f5e8',
                                color: '#2e7d32',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                mb: 0.5
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                            {user.seen_at ? timeDiffer(new Date(), new Date(user.seen_at)) : 'Recently'}
                        </Typography>
                    </Box>
                ) : (
                    <Chip
                        label="Unseen"
                        size="small"
                        sx={{
                            backgroundColor: '#fff3e0',
                            color: '#f57c00',
                            fontSize: '0.75rem',
                            fontWeight: 500
                        }}
                    />
                )}
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={true}
            onClose={() => setShowParticipants(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    maxHeight: '80vh',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e9ecef'
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    pb: 2,
                    borderBottom: '1px solid #e9ecef',
                    backgroundColor: '#fff',
                    position: 'relative'
                }}
            >
                <Box sx={{ pr: 6 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E7C88' }}>
                        Policy Viewers
                    </Typography>
                </Box>
                <IconButton
                    onClick={() => setShowParticipants(false)}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        color: 'text.secondary',
                        bgcolor: 'grey.100',
                        borderRadius: '50%',
                        '&:hover': { bgcolor: 'grey.300' },
                        zIndex: 2
                    }}
                    size="small"
                >
                    <FiX size={18} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {/* Compact Modern Tabs */}
                <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #f0f0f0' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            p: 0.5,
                            width: 'fit-content'
                        }}
                    >
                        <Box
                            onClick={() => handleTabChange(null, 0)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 1,
                                px: 2,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                backgroundColor: activeTab === 0 ? '#1E7C88' : 'transparent',
                                color: activeTab === 0 ? '#fff' : '#666',
                                fontSize: '0.875rem',
                                fontWeight: activeTab === 0 ? 600 : 500,
                                boxShadow: activeTab === 0 ? '0 2px 8px rgba(30, 124, 136, 0.2)' : 'none',
                                '&:hover': {
                                    backgroundColor: activeTab === 0 ? '#176B75' : '#e9ecef',
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            <FiEye size={16} />
                            Seen
                            <Box
                                sx={{
                                    minWidth: 20,
                                    height: 20,
                                    borderRadius: '10px',
                                    backgroundColor: activeTab === 0 ? 'rgba(255, 255, 255, 0.2)' : '#e8f5e8',
                                    color: activeTab === 0 ? '#fff' : '#2e7d32',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    border: activeTab === 0 ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid #d4edda'
                                }}
                            >
                                {seenUsers.length}
                            </Box>
                        </Box>

                        <Box
                            onClick={() => handleTabChange(null, 1)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 1,
                                px: 2,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                backgroundColor: activeTab === 1 ? '#f57c00' : 'transparent',
                                color: activeTab === 1 ? '#fff' : '#666',
                                fontSize: '0.875rem',
                                fontWeight: activeTab === 1 ? 600 : 500,
                                boxShadow: activeTab === 1 ? '0 2px 8px rgba(245, 124, 0, 0.2)' : 'none',
                                '&:hover': {
                                    backgroundColor: activeTab === 1 ? '#e65100' : '#e9ecef',
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            <FiEyeOff size={16} />
                            Unseen
                            <Box
                                sx={{
                                    minWidth: 20,
                                    height: 20,
                                    borderRadius: '10px',
                                    backgroundColor: activeTab === 1 ? 'rgba(255, 255, 255, 0.2)' : '#fff3e0',
                                    color: activeTab === 1 ? '#fff' : '#f57c00',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    border: activeTab === 1 ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid #ffcc80'
                                }}
                            >
                                {unseenUsers.length}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Content */}
                <Box sx={{ p: 2, maxHeight: '50vh', overflowY: 'auto' }}>
                    {activeTab === 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {seenUsers.length > 0 ? (
                                seenUsers.map((user, index) => <UserCard key={index} user={user} isSeen={true} />)
                            ) : (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        py: 4,
                                        color: '#999'
                                    }}
                                >
                                    <FiEye size={48} color="#ddd" />
                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                        No one has seen this policy yet
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    {activeTab === 1 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {unseenUsers.length > 0 ? (
                                unseenUsers.map((user, index) => <UserCard key={index} user={user} isSeen={false} />)
                            ) : (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        py: 4,
                                        color: '#999'
                                    }}
                                >
                                    <FiEyeOff size={48} color="#ddd" />
                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                        Everyone has seen this policy
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PolicyViewers;
